import { type Editor, Extension } from '@tiptap/core';
import { type Node as PMNode } from '@tiptap/pm/model';
import { Plugin, PluginKey, TextSelection } from '@tiptap/pm/state';
import { Decoration, DecorationSet } from '@tiptap/pm/view';

export interface FindMatch {
  from: number;
  to: number;
}

export interface FindState {
  query: string;
  caseSensitive: boolean;
  /** Index of the active match, or -1 when there is none. */
  index: number;
  matches: FindMatch[];
}

const EMPTY: FindState = { query: '', caseSensitive: false, index: -1, matches: [] };
const key = new PluginKey<FindState>('documentFind');
/** A bound so a one-letter query in a huge note cannot freeze the page. */
export const MAX_MATCHES = 5000;

/**
 * Every occurrence of `query` inside ONE text block (a paragraph, heading, table cell ...). Marks
 * do not split a match ("bo" + "ld" is found as "bold"); a match never spans two blocks, so a
 * replacement can never merge or destroy structure. Inline leaves (a line break, an image) count
 * as one character that no query can equal.
 */
export function findMatches(doc: PMNode, query: string, caseSensitive: boolean): FindMatch[] {
  if (query === '') return [];
  const needle = caseSensitive ? query : query.toLowerCase();
  const found: FindMatch[] = [];
  doc.descendants((node, pos) => {
    if (found.length >= MAX_MATCHES) return false;
    if (!node.isTextblock) return true;
    const text = node.textBetween(0, node.content.size, undefined, '￼');
    const hay = caseSensitive ? text : text.toLowerCase();
    // lower-casing can change the length of a few letters; then the offsets are unreliable
    if (hay.length !== text.length) return false;
    for (let at = hay.indexOf(needle); at !== -1; at = hay.indexOf(needle, at + needle.length)) {
      found.push({ from: pos + 1 + at, to: pos + 1 + at + needle.length });
      if (found.length >= MAX_MATCHES) break;
    }
    return false;
  });
  return found;
}

function compute(doc: PMNode, query: string, caseSensitive: boolean, index: number): FindState {
  const matches = findMatches(doc, query, caseSensitive);
  return {
    query,
    caseSensitive,
    matches,
    index: matches.length === 0 ? -1 : Math.min(Math.max(index, 0), matches.length - 1),
  };
}

type Meta = { query: string; caseSensitive: boolean } | { index: number } | { clear: true };

/**
 * Document-local search. It only DECORATES the document (the matches are never part of it), so
 * nothing of it is saved or synchronised; replacing is an ordinary transaction, which is what the
 * collaboration layer and the undo history already handle.
 */
export const FindReplace = Extension.create({
  name: 'findReplace',
  addProseMirrorPlugins: () => [
    new Plugin<FindState>({
      key,
      state: {
        init: () => EMPTY,
        apply(tr, previous) {
          const meta = tr.getMeta(key) as Meta | undefined;
          if (meta) {
            if ('clear' in meta) return EMPTY;
            if ('query' in meta) return compute(tr.doc, meta.query, meta.caseSensitive, 0);
            return { ...previous, index: meta.index };
          }
          if (tr.docChanged && previous.query !== '') {
            return compute(tr.doc, previous.query, previous.caseSensitive, previous.index);
          }
          return previous;
        },
      },
      props: {
        decorations(state) {
          const found = key.getState(state);
          if (!found || found.matches.length === 0) return DecorationSet.empty;
          return DecorationSet.create(
            state.doc,
            found.matches.map((match, i) =>
              Decoration.inline(match.from, match.to, {
                class: i === found.index ? 'find-match find-match-active' : 'find-match',
              }),
            ),
          );
        },
      },
    }),
  ],
});

export function findState(editor: Editor): FindState {
  return key.getState(editor.state) ?? EMPTY;
}

function dispatch(editor: Editor, meta: Meta, select = false) {
  const tr = editor.state.tr.setMeta(key, meta);
  if (select && 'index' in meta) {
    const match = findState(editor).matches[meta.index];
    if (match) tr.setSelection(TextSelection.create(editor.state.doc, match.from, match.to));
    tr.scrollIntoView();
  }
  editor.view.dispatch(tr);
}

export function setFind(editor: Editor, query: string, caseSensitive: boolean) {
  dispatch(editor, { query, caseSensitive });
}

export function clearFind(editor: Editor) {
  dispatch(editor, { clear: true });
}

/** Moves the active match (wrapping around) and selects it so it is scrolled into view. */
export function stepFind(editor: Editor, direction: 1 | -1) {
  const { matches, index } = findState(editor);
  if (matches.length === 0) return;
  dispatch(editor, { index: (index + direction + matches.length) % matches.length }, true);
}

/** Replaces the active match and moves on. Formatting of the replaced text carries over. */
export function replaceCurrent(editor: Editor, replacement: string): boolean {
  const { matches, index } = findState(editor);
  const match = matches[index];
  if (!match || !editor.isEditable) return false;
  editor.view.dispatch(editor.state.tr.insertText(replacement, match.from, match.to));
  const after = findState(editor);
  if (after.matches.length > 0) {
    // the next match is the first one that starts at or after the replaced text
    const next = after.matches.findIndex((m) => m.from >= match.from + replacement.length);
    dispatch(editor, { index: next === -1 ? 0 : next }, true);
  }
  return true;
}

/**
 * Replaces every match in ONE transaction (one undo step, one synchronised update). Going from the
 * last match to the first keeps the earlier positions valid while the text changes length.
 */
export function replaceAll(editor: Editor, replacement: string): number {
  const { matches } = findState(editor);
  if (matches.length === 0 || !editor.isEditable) return 0;
  const tr = editor.state.tr;
  for (const match of [...matches].reverse()) tr.insertText(replacement, match.from, match.to);
  editor.view.dispatch(tr);
  return matches.length;
}
